import { defineStore, acceptHMRUpdate } from 'pinia'
import axios from 'axios'

const CLINICS_TAKE_LIMIT = 1000 // matches ->take(1000) in BaseBookingClinicController::clinics
const WORLD_BOUNDS = {
    topRight: { lat: 90, lng: 180 },
    bottomLeft: { lat: -90, lng: -180 }
}

export const useBookingStore = defineStore('booking', {
    state: () => ({
        initialized: false,
        view: 'map', // map || list || times
        productAreas: [],
        productArea: null,
        durations: [],
        query: '',
        clinics: [],
        bounds: [],
        boundsTypes: [],
        currentPosition: null,
        zoom: 6,
        singleResultClinic: null,
        timeSlot: {
            time: null,
            clinics: [],
            duration: null
        },
        trigTimeHighlight: false,
        hasSearch: null,
        autocompleteSelectedPhrase: '',
        clinicsCache: null, // { key, clinics }
        clinicsRequestInFlight: false,
        clinicsViewport: null // { topRight, bottomLeft }
    }),
    actions: {
        setView (view) {
            this.view = view
        },
        setCurrentPosition (position) {
            this.currentPosition = position
        },

        getMarkers (topRight, bottomLeft, force = false) {
            this.clinicsViewport = { topRight, bottomLeft }

            if (this.clinicsCache && this.clinicsCache.key === this.clinicsCacheKey()) {
                this.setVisibleClinics(force)
                return
            }

            if (!this.clinicsRequestInFlight) {
                this.fetchAllClinics()
            }
        },

        fetchAllClinics () {
            const key = this.clinicsCacheKey()
            this.clinicsRequestInFlight = true

            axios.post('/booking/clinics', {
                currentPosition: this.currentPosition,
                topRight: WORLD_BOUNDS.topRight,
                bottomLeft: WORLD_BOUNDS.bottomLeft,
                productArea: this.productArea,
                singleResultClinic: null
            })
                .then(response => {
                    if (response.data.clinics.length >= CLINICS_TAKE_LIMIT) {
                        console.warn('booking/clinics hit the result limit, some clinics are missing')
                    }

                    this.clinicsCache = { key, clinics: response.data.clinics }
                })
                .finally(() => {
                    this.clinicsRequestInFlight = false

                    // productArea/position may have changed while loading
                    if (key !== this.clinicsCacheKey()) {
                        this.fetchAllClinics()
                    } else if (this.clinicsCache && this.clinicsCache.key === key) {
                        // Always assign on a fresh response so the map rebuilds markers after a reset
                        this.setVisibleClinics(true)
                    }
                })
        },

        setVisibleClinics (force = false) {
            if (!this.clinicsCache || !this.clinicsViewport) {
                return
            }

            const { topRight, bottomLeft } = this.clinicsViewport
            const visible = this.clinicsCache.clinics.filter(clinic =>
                pointInBox(clinic._geo, topRight, bottomLeft) &&
                (!this.singleResultClinic || clinic.id === this.singleResultClinic.id))

            const currentIds = this.clinics.map(c => c.id).join(',')
            const visibleIds = visible.map(c => c.id).join(',')

            if (force || currentIds !== visibleIds) {
                this.clinics = visible
            }
        },

        clinicsCacheKey () {
            return JSON.stringify([this.productArea, this.currentPosition])
        },

        getBounds (center) {
            axios.post('/booking/getBounds', {
                center: center,
                productArea: this.productArea
            })
                .then(response => {
                    console.log('getBounds complete')
                    console.log(response.data)

                    this.bounds = response.data.bounds
                })
        }
    }
})

function pointInBox (point, topRight, bottomLeft) {
    return point.lat <= topRight.lat && point.lat >= bottomLeft.lat &&
        point.lng <= topRight.lng && point.lng >= bottomLeft.lng
}

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBookingStore, import.meta.hot))
}
